"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";

const LoadingScreen = ({ onLoadingComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [fragments, setFragments] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [particles, setParticles] = useState([]);
  const [heroSweepProgress, setHeroSweepProgress] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
  const containerRef = useRef(null);
  const animationRef = useRef();

  // Track viewport dimensions
  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Product images with enhanced properties
  const productDefinitions = [
    {
      id: "Key chain",
      src: "https://cdn.acsdev.in/ManaPrint/1777007092_69eaf9f4c5a2c_ChatGPT Image Apr 2, 2026, 03_06_38 PM.png",
      alt: "Custom Key chain",
      size: "large",
      category: "Key chains",
    },
    {
      id: "Pillow Cover",
      src: "https://cdn.acsdev.in/ManaPrint/1773816737_69ba4ba19290f_ChatGPT Image Mar 16, 2026, 07_02_19 PM.png",
      alt: "Custom Pillow Cover",
      size: "medium",
      category: "Pillow Cover",
    },
    {
      id: "Swamy Photo Frame",
      src: "https://cdn.acsdev.in/ManaPrint/1768298690_81bejhF8JfL._SY741_.jpg",
      alt: "Custom Swamy Photo Frame",
      size: "large",
      category: "Photo Frames",
    },
    {
      id: "Design Photo Frame",
      src: "https://cdn.acsdev.in/ManaPrint/1773124304_69afbad0b4b2a.jpg",
      alt: "Custom Design Photo Frame",
      size: "small",
      category: "Photo Frames",
    },
    {
      id: "Pencils with names",
      src: "https://cdn.acsdev.in/ManaPrint/1773047694_69ae8f8e4ea86.jpg",
      alt: "Custom Pencils with names",
      size: "large",
      category: "Pencils",
    },
    {
      id: "Mobile case",
      src: "https://cdn.acsdev.in/ManaPrint/1774346396_69c2609c265e0_Layer37_c161885a-daa4-4439-84e8-ef7946a8cffe.webp",
      alt: "Canvas Mobile case",
      size: "large",
      category: "Mobile case",
    },
    {
      id: "phone-case",
      src: "https://cdn.acsdev.in/ManaPrint/1766385336_71SfwA7M2mL (1).jpg",
      alt: "Custom phone case",
      size: "small",
      category: "accessories",
    },
    {
      id: "Album",
      src: "https://cdn.acsdev.in/ManaPrint/1777030110_69eb53dececb3_51j+SQErIHL.jpg",
      alt: "Custom album",
      size: "medium",
      category: "apparel",
    },
  ];

  // Letter fragment definitions
  const letterFragments = {
    M: [
      {
        type: "rect",
        path: "M0,0 L8,0 L8,40 L0,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0,
      },
      {
        type: "triangle",
        path: "M8,0 L20,18 L8,18 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.1,
      },
      {
        type: "triangle",
        path: "M20,18 L32,0 L32,18 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.15,
      },
      {
        type: "rect",
        path: "M32,0 L40,0 L40,40 L32,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.05,
      },
    ],
    A: [
      {
        type: "triangle",
        path: "M0,40 L8,40 L20,0 L12,0 Z",
        targetX: 0,
        targetY: 0,
        delay: 0,
      },
      {
        type: "triangle",
        path: "M20,0 L28,0 L40,40 L32,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.1,
      },
      {
        type: "rect",
        path: "M10,22 L30,22 L30,30 L10,30 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.2,
      },
    ],
    N: [
      {
        type: "rect",
        path: "M0,0 L8,0 L8,40 L0,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0,
      },
      {
        type: "polygon",
        path: "M8,0 L16,0 L32,32 L32,0 L40,0 L40,40 L32,40 L8,8 L8,40 L0,40 L0,0 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.1,
      },
      {
        type: "rect",
        path: "M32,0 L40,0 L40,40 L32,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.05,
      },
    ],
    P: [
      {
        type: "rect",
        path: "M0,0 L8,0 L8,40 L0,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0,
      },
      {
        type: "rect",
        path: "M8,0 L28,0 L28,8 L8,8 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.1,
      },
      {
        type: "rect",
        path: "M28,0 L36,0 L36,20 L28,20 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.15,
      },
      {
        type: "rect",
        path: "M8,20 L36,20 L36,28 L8,28 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.2,
      },
    ],
    R: [
      {
        type: "rect",
        path: "M0,0 L8,0 L8,40 L0,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0,
      },
      {
        type: "rect",
        path: "M8,0 L28,0 L28,8 L8,8 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.1,
      },
      {
        type: "rect",
        path: "M28,0 L36,0 L36,20 L28,20 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.15,
      },
      {
        type: "rect",
        path: "M8,20 L36,20 L36,28 L8,28 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.2,
      },
      {
        type: "polygon",
        path: "M20,28 L36,40 L28,40 L16,32 L8,32 L8,28 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.25,
      },
    ],
    I: [
      {
        type: "rect",
        path: "M0,0 L32,0 L32,8 L0,8 Z",
        targetX: 0,
        targetY: 0,
        delay: 0,
      },
      {
        type: "rect",
        path: "M12,8 L20,8 L20,32 L12,32 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.1,
      },
      {
        type: "rect",
        path: "M0,32 L32,32 L32,40 L0,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.05,
      },
    ],
    T: [
      {
        type: "rect",
        path: "M0,0 L40,0 L40,8 L0,8 Z",
        targetX: 0,
        targetY: 0,
        delay: 0,
      },
      {
        type: "rect",
        path: "M16,8 L24,8 L24,40 L16,40 Z",
        targetX: 0,
        targetY: 0,
        delay: 0.1,
      },
    ],
  };

  const letters = ["M", "A", "N", "A", "P", "R", "I", "N", "T"];

  // Helper function to clamp position within viewport bounds
  const clampToViewport = useCallback(
    (x, y, imageSize, padding = 50) => {
      const { width, height } = dimensions;
      const halfSize = imageSize / 2;

      const clampedX = Math.max(
        halfSize + padding,
        Math.min(width - halfSize - padding, x),
      );
      const clampedY = Math.max(
        halfSize + padding,
        Math.min(height - halfSize - padding, y),
      );

      return { x: clampedX, y: clampedY };
    },
    [dimensions],
  );

  // Generate product images with sequential half-moon positioning
  const generateProductImages = useCallback(() => {
    const { width, height } = dimensions;
    const centerX = width / 2;
    const centerY = height / 2;

    // Starting position - right top corner
    const startX = width - 50;
    const startY = 50;

    // Final size for all images
    const finalSize = 80;

    const images = [];

    productDefinitions.forEach((product, index) => {
      // Determine which half-moon this image belongs to
      const totalImages = productDefinitions.length;
      const upperHalfMoonCount = Math.ceil(totalImages * 0.6); // 60% go to upper half-moon
      const isUpperHalfMoon = index < upperHalfMoonCount;

      // Calculate final position in half-moon formation
      let finalX, finalY;

      if (isUpperHalfMoon) {
        // Upper half-moon (above title) - wider and higher
        const upperIndex = index;
        const upperAngle =
          Math.PI + (upperIndex / (upperHalfMoonCount - 1)) * Math.PI;
        const upperRadius = 280; // Increased from 180 for wider spread
        finalX = centerX + Math.cos(upperAngle) * upperRadius;
        finalY = centerY - 200 + Math.sin(upperAngle) * upperRadius * 0.3; // Higher position, more concave
      } else {
        // Lower half-moon (below title) - wider and lower
        const lowerIndex = index - upperHalfMoonCount;
        const lowerCount = totalImages - upperHalfMoonCount;
        const lowerAngle = (lowerIndex / (lowerCount - 1)) * Math.PI;
        const lowerRadius = 280; // Increased from 180 for wider spread
        finalX = centerX + Math.cos(lowerAngle) * lowerRadius;
        finalY = centerY + 160 + Math.sin(lowerAngle) * lowerRadius * 0.3; // Moved up from 200 to 160
      }

      // Create a convoy path that visits all final positions
      const allFinalPositions = [];

      // Add all positions to the convoy route
      productDefinitions.forEach((_, idx) => {
        const isUpper = idx < upperHalfMoonCount;
        let posX, posY;

        if (isUpper) {
          const upperIdx = idx;
          const upperAngle =
            Math.PI + (upperIdx / (upperHalfMoonCount - 1)) * Math.PI;
          const upperRadius = 280;
          posX = centerX + Math.cos(upperAngle) * upperRadius;
          posY = centerY - 200 + Math.sin(upperAngle) * upperRadius * 0.3;
        } else {
          const lowerIdx = idx - upperHalfMoonCount;
          const lowerCount = productDefinitions.length - upperHalfMoonCount;
          const lowerAngle = (lowerIdx / (lowerCount - 1)) * Math.PI;
          const lowerRadius = 280;
          posX = centerX + Math.cos(lowerAngle) * lowerRadius;
          posY = centerY + 160 + Math.sin(lowerAngle) * lowerRadius * 0.3;
        }

        allFinalPositions.push({ x: posX, y: posY, imageIndex: idx });
      });

      // Create ordered convoy path: Lower Right→Left, then Upper Left→Right
      const lowerPositions = [];
      const upperPositions = [];

      // Separate positions into lower and upper half-moons
      allFinalPositions.forEach((pos) => {
        const isUpper = pos.imageIndex < upperHalfMoonCount;
        if (isUpper) {
          upperPositions.push(pos);
        } else {
          lowerPositions.push(pos);
        }
      });

      // Sort lower positions from right to left (X descending)
      lowerPositions.sort((a, b) => b.x - a.x);

      // Sort upper positions from left to right (X ascending)
      upperPositions.sort((a, b) => a.x - b.x);

      // Combine in order: Lower (right to left) + Upper (left to right)
      const orderedPath = [...lowerPositions, ...upperPositions];

      // Replace allFinalPositions with ordered path
      allFinalPositions.length = 0;
      allFinalPositions.push(...orderedPath);

      images.push({
        ...product,
        index,
        startX,
        startY,
        finalX,
        finalY,
        finalSize,
        isUpperHalfMoon,
        sequenceDelay: index * 0.08, // Sequential delay for entry
        dropOffIndex: allFinalPositions.findIndex(
          (pos) => pos.imageIndex === index,
        ), // When this image drops off
        convoyPath: allFinalPositions, // The full convoy route
        currentX: startX,
        currentY: startY,
        currentSize: 20, // Start very small
        opacity: 0,
        rotation: 0,
        blur: 2,
        glow: 0,
        hasDroppedOff: false,
      });
    });

    return images;
  }, [dimensions]);

  // Generate ambient particles
  const generateParticles = useCallback(() => {
    const { width, height } = dimensions;
    const particleCount = Math.floor((width * height) / 25000);

    return Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      x: Math.random() * width,
      y: Math.random() * height,
      size: 1 + Math.random() * 3,
      speed: 0.2 + Math.random() * 0.5,
      opacity: 0.1 + Math.random() * 0.4,
      color:
        Math.random() > 0.6
          ? "#d4af7f"
          : Math.random() > 0.5
            ? "#2dd4bf"
            : "#fb923c",
      drift: (Math.random() - 0.5) * 2,
    }));
  }, [dimensions]);

  // Generate letter fragments
  const generateFragments = useCallback(() => {
    const newFragments = [];
    const containerWidth = 900;
    const containerHeight = 400;
    const letterSpacing = 85;
    const startX = (containerWidth - letters.length * letterSpacing) / 2;

    letters.forEach((letter, letterIndex) => {
      const letterX = startX + letterIndex * letterSpacing;
      const letterY = containerHeight / 2 - 20;
      const letterDef = letterFragments[letter];

      letterDef.forEach((fragmentDef, fragmentIndex) => {
        const angle = Math.random() * Math.PI * 2;
        const distance = 300 + Math.random() * 500;
        const randomX = letterX + Math.cos(angle) * distance;
        const randomY = letterY + Math.sin(angle) * distance;
        const randomRotation = (Math.random() - 0.5) * 720;

        newFragments.push({
          id: `${letter}-${letterIndex}-${fragmentIndex}`,
          type: fragmentDef.type,
          startX: randomX,
          startY: randomY,
          targetX: letterX + fragmentDef.targetX,
          targetY: letterY + fragmentDef.targetY,
          rotation: randomRotation,
          targetRotation: (Math.random() - 0.5) * 10,
          scale: 0.2 + Math.random() * 0.3,
          targetScale: 1,
          opacity: 0.3 + Math.random() * 0.4,
          targetOpacity: 1,
          letterIndex,
          fragmentIndex,
          path: fragmentDef.path,
          delay: fragmentDef.delay,
          depth: Math.random() * 0.3,
          jitterX: 0,
          jitterY: 0,
        });
      });
    });

    return newFragments;
  }, []);

  // Easing functions
  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const elasticOut = (t) => {
    const c4 = (2 * Math.PI) / 3;
    return t === 0
      ? 0
      : t === 1
        ? 1
        : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  };
  const backOut = (t) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  };

  const getPhaseProgress = (phase, start, end) => {
    if (phase < start) return 0;
    if (phase > end) return 1;
    return (phase - start) / (end - start);
  };

  // Enhanced product image interpolation with convoy drop-off animation
  const interpolateProductImage = useCallback(
    (product, progress, time) => {
      // Animation phases with convoy timing
      const sequenceStart = 10 + product.sequenceDelay;
      const entryProgress = getPhaseProgress(
        progress,
        sequenceStart,
        sequenceStart + 10,
      );
      const convoyProgress = getPhaseProgress(
        progress,
        sequenceStart + 10,
        sequenceStart + 50,
      );
      const stabilizeProgress = getPhaseProgress(
        progress,
        sequenceStart + 50,
        sequenceStart + 60,
      );
      const heroProgress = getPhaseProgress(progress, 90, 100);

      let currentX = product.startX;
      let currentY = product.startY;
      let currentSize = 20;
      let currentOpacity = 0;
      let currentBlur = 2;
      let currentGlow = 0;
      let currentRotation = 0;

      // Phase 1: Entry from right top corner (sequential)
      if (entryProgress > 0) {
        const easedProgress = easeInOutCubic(entryProgress);

        currentOpacity = easedProgress * 0.8;
        currentSize = 20 + easedProgress * 30; // Grow from 20 to 50
        currentBlur = 2 - easedProgress * 1.5;
        currentGlow = easedProgress * 0.3;
        currentRotation = easedProgress * 180;

        currentX = product.startX;
        currentY = product.startY;
      }

      // Phase 2: Convoy movement with sequential drop-offs
      if (convoyProgress > 0 && !product.hasDroppedOff) {
        const totalConvoyTime = 40; // Total time for convoy movement
        const timePerPosition = totalConvoyTime / product.convoyPath.length;
        const currentPositionIndex = Math.floor(
          convoyProgress * product.convoyPath.length,
        );
        const positionProgress =
          (convoyProgress * product.convoyPath.length) % 1;

        // Check if this image should drop off at current position
        if (
          currentPositionIndex >= product.dropOffIndex &&
          !product.hasDroppedOff
        ) {
          product.hasDroppedOff = true;
          currentX = product.finalX;
          currentY = product.finalY;
          currentSize = product.finalSize;
          currentOpacity = 1;
          currentBlur = 0;
          currentGlow = 0.7;
          currentRotation = 360;
        } else if (currentPositionIndex < product.convoyPath.length - 1) {
          // Continue moving in convoy
          const currentPos = product.convoyPath[currentPositionIndex];
          const nextPos =
            product.convoyPath[currentPositionIndex + 1] || currentPos;

          const easedProgress = easeInOutCubic(positionProgress);
          currentX = currentPos.x + (nextPos.x - currentPos.x) * easedProgress;
          currentY = currentPos.y + (nextPos.y - currentPos.y) * easedProgress;

          currentSize =
            50 + (currentPositionIndex / product.convoyPath.length) * 30;
          currentOpacity =
            0.8 + (currentPositionIndex / product.convoyPath.length) * 0.2;
          currentBlur = 0.5;
          currentGlow =
            0.3 + (currentPositionIndex / product.convoyPath.length) * 0.4;
          currentRotation =
            180 + (currentPositionIndex / product.convoyPath.length) * 180;
        }
      }

      // If already dropped off, stay at final position
      if (product.hasDroppedOff) {
        currentX = product.finalX;
        currentY = product.finalY;
        currentSize = product.finalSize;
        currentOpacity = 1;
        currentBlur = 0;
        currentGlow = 0.7;
        currentRotation = 360;
      }

      // Phase 3: Stabilize in final formation
      if (stabilizeProgress > 0 && product.hasDroppedOff) {
        const easedProgress = easeInOutCubic(stabilizeProgress);

        currentGlow = 0.7 - easedProgress * 0.2;

        // Add gentle floating motion
        const floatX = Math.sin(time * 0.5 + product.index) * 3;
        const floatY = Math.cos(time * 0.3 + product.index) * 2;
        currentX = product.finalX + floatX;
        currentY = product.finalY + floatY;
      }

      // Phase 4: Hero moment fade
      if (heroProgress > 0) {
        const easedProgress = easeInOutCubic(heroProgress);

        currentX = product.finalX;
        currentY = product.finalY;
        currentSize = product.finalSize * (1 - easedProgress * 0.3);
        currentOpacity = 1 - easedProgress * 0.7;
        currentBlur = easedProgress * 2;
        currentGlow = 0.5 * (1 - easedProgress);
        currentRotation = 360;
      }

      // Ensure position is within viewport bounds
      const clampedPosition = clampToViewport(currentX, currentY, currentSize);

      return {
        ...product,
        x: clampedPosition.x,
        y: clampedPosition.y,
        size: currentSize,
        opacity: currentOpacity,
        blur: currentBlur,
        glow: currentGlow,
        rotation: currentRotation,
      };
    },
    [clampToViewport],
  );

  // Fragment interpolation
  const interpolateFragment = useCallback((fragment, progress) => {
    const chaosProgress = getPhaseProgress(progress, 0, 30);
    const attractionProgress = getPhaseProgress(progress, 30, 60);
    const assemblyProgress = getPhaseProgress(progress, 60, 85);
    const lockProgress = getPhaseProgress(progress, 85, 95);
    const heroProgress = getPhaseProgress(progress, 95, 100);

    let currentX = fragment.startX;
    let currentY = fragment.startY;
    let currentRotation = fragment.rotation;
    let currentScale = fragment.scale;
    let currentOpacity = fragment.opacity;
    let blur = 2;
    let glow = 0;

    if (chaosProgress > 0) {
      const drift = Math.sin(Date.now() * 0.001 + fragment.letterIndex) * 3;
      currentX += drift;
      currentY += Math.cos(Date.now() * 0.0008 + fragment.fragmentIndex) * 2;
      currentRotation += Math.sin(Date.now() * 0.0005) * 8;
    }

    if (attractionProgress > 0) {
      const easedProgress = easeInOutCubic(attractionProgress);
      currentX =
        fragment.startX +
        (fragment.targetX - fragment.startX) * easedProgress * 0.6;
      currentY =
        fragment.startY +
        (fragment.targetY - fragment.startY) * easedProgress * 0.6;
      currentRotation =
        fragment.rotation +
        (fragment.targetRotation - fragment.rotation) * easedProgress * 0.3;
      blur = 2 - easedProgress * 0.5;
    }

    if (assemblyProgress > 0) {
      const delayedProgress = Math.max(0, assemblyProgress - fragment.delay);
      const adjustedProgress = Math.min(delayedProgress * 1.3, 1);
      const easedProgress = backOut(adjustedProgress);

      const overshoot = easedProgress > 1 ? (easedProgress - 1) * 0.1 : 0;
      currentX =
        fragment.startX +
        (fragment.targetX - fragment.startX) * (0.6 + easedProgress * 0.4) +
        overshoot * 5;
      currentY =
        fragment.startY +
        (fragment.targetY - fragment.startY) * (0.6 + easedProgress * 0.4) +
        overshoot * 3;
      currentRotation =
        fragment.rotation +
        (fragment.targetRotation - fragment.rotation) *
          (0.3 + easedProgress * 0.7);
      currentScale =
        fragment.scale +
        (fragment.targetScale - fragment.scale) * easedProgress;
      currentOpacity =
        fragment.opacity +
        (fragment.targetOpacity - fragment.opacity) * easedProgress;

      glow = adjustedProgress * 0.6;
      blur = Math.max(0.5, 1.5 - adjustedProgress * 1);
    }

    if (lockProgress > 0) {
      const easedProgress = easeInOutCubic(lockProgress);

      const jitterIntensity = (1 - easedProgress) * 2;
      fragment.jitterX = (Math.random() - 0.5) * jitterIntensity;
      fragment.jitterY = (Math.random() - 0.5) * jitterIntensity;

      const scalePhase = easedProgress * 2;
      let scaleMultiplier =
        scalePhase < 1 ? 1 + scalePhase * 0.03 : 1.03 - (scalePhase - 1) * 0.03;

      currentX = fragment.targetX + fragment.jitterX;
      currentY = fragment.targetY + fragment.jitterY;
      currentRotation = fragment.targetRotation;
      currentScale = fragment.targetScale * scaleMultiplier;
      currentOpacity = fragment.targetOpacity;

      glow = (1 - easedProgress) * 0.6;
      blur = 0;
    }

    if (heroProgress > 0) {
      currentX = fragment.targetX;
      currentY = fragment.targetY;
      currentRotation = fragment.targetRotation;
      currentScale = fragment.targetScale;
      currentOpacity = fragment.targetOpacity;
      blur = 0;
      glow = 0;
    }

    return {
      ...fragment,
      startX: currentX,
      startY: currentY,
      rotation: currentRotation,
      scale: currentScale,
      opacity: currentOpacity,
      blur,
      glow,
    };
  }, []);

  // Initialize
  useEffect(() => {
    setFragments(generateFragments());
    setProductImages(generateProductImages());
    setParticles(generateParticles());
  }, [generateFragments, generateProductImages, generateParticles]);

  // Update on dimension change
  useEffect(() => {
    setProductImages(generateProductImages());
    setParticles(generateParticles());
  }, [dimensions, generateProductImages, generateParticles]);

  // Animation loop
  useEffect(() => {
    const startTime = Date.now();
    const duration = 3500; // Increased duration for sequential animation

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1) * 100;
      setAnimationPhase(progress);
      setLoadingProgress(progress);

      if (progress >= 95) {
        setHeroSweepProgress((progress - 95) / 5);
      }

      if (progress < 100) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(() => {
            if (onLoadingComplete) onLoadingComplete();
          }, 800);
        }, 1000);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [onLoadingComplete]);

  if (!isVisible) {
    return (
      <div
        className="fixed inset-0 z-50 transition-opacity duration-800 ease-in-out opacity-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 30%, #134e4a 0%, #042f2e 60%, #021c1b 100%)",
        }}
        aria-hidden="true"
      />
    );
  }

  const time = Date.now() * 0.001;
  const currentFragments = fragments.map((f) =>
    interpolateFragment(f, animationPhase),
  );
  const currentProductImages = productImages.map((p) =>
    interpolateProductImage(p, animationPhase, time),
  );
  const isLocked = animationPhase >= 85;
  const isHeroMoment = animationPhase >= 95;

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap"
      />
      <style>{`
        @keyframes heroSweep {
          0% { transform: translateX(-100%) skewX(-15deg); opacity: 0; }
          20% { opacity: 0.8; }
          80% { opacity: 0.8; }
          100% { transform: translateX(200%) skewX(-15deg); opacity: 0; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.8; }
        }

        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        @keyframes orbit-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(212, 175, 127, 0.3), 0 0 40px rgba(45, 212, 191, 0.1); }
          50% { box-shadow: 0 0 30px rgba(212, 175, 127, 0.5), 0 0 60px rgba(45, 212, 191, 0.2); }
        }

        @keyframes particle-drift {
          0% { transform: translateY(0) translateX(0); }
          100% { transform: translateY(-100vh) translateX(var(--drift)); }
        }

        .hero-sweep {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(212, 175, 127, 0.1) 30%,
            rgba(255, 255, 255, 0.3) 50%,
            rgba(212, 175, 127, 0.1) 70%,
            transparent 100%
          );
          animation: heroSweep 1.5s ease-out forwards;
        }

        .product-card {
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          will-change: transform, opacity, filter;
          backface-visibility: hidden;
        }

        .product-card::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(135deg, rgba(212, 175, 127, 0.4), rgba(45, 212, 191, 0.3), rgba(251, 146, 60, 0.4));
          border-radius: 18px;
          z-index: -1;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .product-card:hover::before {
          opacity: 1;
        }

        .product-card:hover {
          transform: scale(1.15) !important;
        }

        .product-image-inner {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 14px;
          border: 2px solid rgba(45, 212, 191, 0.2);
        }

        .category-badge {
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          padding: 2px 8px;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          border-radius: 10px;
          background: linear-gradient(135deg, #0f766e, #134e4a);
          color: #f0fdfa;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .fragment-container {
          width: 900px;
          height: 400px;
        }

        @media (max-width: 480px) {
          .fragment-container { transform: scale(0.38); }
        }
        @media (min-width: 481px) and (max-width: 768px) {
          .fragment-container { transform: scale(0.55); }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .fragment-container { transform: scale(0.75); }
        }

        .progress-bar-fill {
          background: linear-gradient(
            90deg,
            #f5e6c8 0%,
            #d4af7f 30%,
            #fb923c 50%,
            #d4af7f 70%,
            #f5e6c8 100%
          );
          background-size: 200% 100%;
          animation: shimmer 2s linear infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div
        className="fixed inset-0 z-50 overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, #134e4a 0%, #042f2e 50%, #021c1b 100%)",
        }}
        role="dialog"
        aria-label="Loading screen"
        aria-live="polite"
      >
        {/* Ambient Particles */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
        >
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full"
              style={{
                left: particle.x,
                top: `${(particle.y + Date.now() * particle.speed * 0.05) % dimensions.height}px`,
                width: particle.size,
                height: particle.size,
                backgroundColor: particle.color,
                opacity: particle.opacity,
                "--drift": `${particle.drift * 50}px`,
              }}
            />
          ))}
        </div>

        {/* Curved Panels */}
        <div className="absolute left-0 right-0 top-0 z-10">
          <svg
            viewBox="0 0 1440 280"
            className="w-full h-[100px] sm:h-[130px] md:h-[160px] lg:h-[180px]"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="topGrad" x1="0" x2="1">
                <stop offset="0%" stopColor="#115e59" />
                <stop offset="50%" stopColor="#0f766e" />
                <stop offset="100%" stopColor="#134e4a" />
              </linearGradient>
            </defs>
            <path
              d="M0,0 L1440,0 L1440,160 Q1080,220 720,200 Q360,180 0,160 Z"
              fill="url(#topGrad)"
            />
          </svg>
        </div>

        <div className="absolute left-0 right-0 bottom-0 z-10">
          <svg
            viewBox="0 0 1440 280"
            className="w-full h-[100px] sm:h-[130px] md:h-[160px] lg:h-[180px] rotate-180"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="bottomGrad" x1="0" x2="1">
                <stop offset="0%" stopColor="#0d9488" />
                <stop offset="50%" stopColor="#115e59" />
                <stop offset="100%" stopColor="#0f766e" />
              </linearGradient>
            </defs>
            <path
              d="M0,0 L1440,0 L1440,100 Q1080,150 720,140 Q360,130 0,100 Z"
              fill="url(#bottomGrad)"
            />
          </svg>
        </div>

        {/* Sequential Half-Moon Product Images */}
        <div className="absolute inset-0 z-15" aria-hidden="true">
          {currentProductImages.map((product) => (
            <div
              key={product.id}
              className="absolute product-card"
              style={{
                left: product.x - product.size / 2,
                top: product.y - product.size / 2,
                width: product.size,
                height: product.size,
                opacity: product.opacity,
                transform: `rotate(${product.rotation}deg)`,
                filter: `blur(${product.blur}px)`,
                boxShadow:
                  product.glow > 0
                    ? `0 0 ${product.glow * 30}px rgba(212, 175, 127, ${product.glow * 0.6}),
                     0 0 ${product.glow * 60}px rgba(45, 212, 191, ${product.glow * 0.3}),
                     0 8px 32px rgba(0, 0, 0, 0.4)`
                    : "0 8px 32px rgba(0, 0, 0, 0.3)",
                zIndex: 15 + product.index,
              }}
            >
              <img
                src={product.src}
                alt={product.alt}
                className="product-image-inner"
                loading="eager"
                draggable="false"
              />
              <span className="category-badge">{product.category}</span>
            </div>
          ))}
        </div>

        {/* MANAPRINT Logo */}
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div ref={containerRef} className="relative fragment-container">
            <svg
              width="900"
              height="400"
              viewBox="0 0 900 400"
              style={{ overflow: "visible" }}
            >
              <defs>
                <linearGradient
                  id="brandGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#f5e6c8" />
                  <stop offset="50%" stopColor="#d4af7f" />
                  <stop offset="100%" stopColor="#b8965a" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {currentFragments.map((fragment) => (
                <g
                  key={fragment.id}
                  transform={`translate(${fragment.startX}, ${fragment.startY}) rotate(${fragment.rotation}) scale(${fragment.scale})`}
                  opacity={fragment.opacity}
                >
                  <path
                    d={fragment.path}
                    fill="url(#brandGradient)"
                    style={{
                      filter:
                        fragment.glow > 0
                          ? `drop-shadow(0 0 ${fragment.glow * 15}px rgba(212, 175, 127, ${fragment.glow})) blur(${fragment.blur}px)`
                          : `blur(${fragment.blur}px)`,
                      stroke: isLocked ? "none" : "rgba(212, 175, 127, 0.15)",
                      strokeWidth: isLocked ? 0 : 0.5,
                    }}
                  />
                </g>
              ))}
            </svg>

            {isHeroMoment && <div className="hero-sweep" />}
          </div>
        </div>

        {/* Subtitle */}
        <div className="absolute left-0 right-0 z-20" style={{ top: "62%" }}>
          <div className="text-center px-4">
            <p
              className="text-teal-100 text-lg sm:text-xl md:text-2xl opacity-80"
              style={{ fontFamily: "'Great Vibes', cursive" }}
            >
              Creating your custom prints with love...
            </p>
          </div>
        </div>

        {/* Progress Section */}
        <div className="absolute left-0 right-0 bottom-0 z-20 pb-6 sm:pb-8 md:pb-6">
          <div className="w-full max-w-md mx-auto px-6">
            <div className="relative mb-3">
              <div
                className="w-full h-1.5 rounded-full overflow-hidden bg-teal-900/60"
                role="progressbar"
                aria-valuenow={Math.round(loadingProgress)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full rounded-full progress-bar-fill transition-all duration-300"
                  style={{
                    width: `${loadingProgress}%`,
                    boxShadow: isLocked
                      ? "0 0 20px rgba(212, 175, 127, 0.8), 0 0 40px rgba(251, 146, 60, 0.4)"
                      : "0 0 12px rgba(212, 175, 127, 0.5)",
                  }}
                />
              </div>
              <span className="absolute -bottom-6 right-0 text-xs font-medium tracking-wider text-teal-200/80">
                {Math.round(loadingProgress)}%
              </span>
            </div>

            <div className="flex justify-center space-x-4 pt-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      i === 0 ? "#d4af7f" : i === 1 ? "#2dd4bf" : "#fb923c",
                    animation: "pulse 2s ease-in-out infinite",
                    animationDelay: `${i * 0.3}s`,
                    opacity: isLocked ? 0.9 : 0.5,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Corner Decorations */}
        <div
          className="absolute top-6 left-6 w-14 h-14 border-l-2 border-t-2 border-teal-400/30 rounded-tl-lg"
          style={{ animation: "float 5s ease-in-out infinite" }}
          aria-hidden="true"
        />
        <div
          className="absolute top-6 right-6 w-14 h-14 border-r-2 border-t-2 border-amber-400/30 rounded-tr-lg"
          style={{
            animation: "float 5s ease-in-out infinite",
            animationDelay: "1s",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute bottom-6 left-6 w-14 h-14 border-l-2 border-b-2 border-amber-400/30 rounded-bl-lg"
          style={{
            animation: "float 5s ease-in-out infinite",
            animationDelay: "2s",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute bottom-6 right-6 w-14 h-14 border-r-2 border-b-2 border-teal-400/30 rounded-br-lg"
          style={{
            animation: "float 5s ease-in-out infinite",
            animationDelay: "3s",
          }}
          aria-hidden="true"
        />
      </div>
    </>
  );
};

export default LoadingScreen;
